import React, { useState, useCallback } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

/**
 * SortableTable — table with clickable sortable headers and a skeleton loader.
 *
 * Props:
 *   columns       {Array}    — [{ key, label, sortable?, align?, maxWidth?, wrap?, render? }]
 *   data          {Array}    — rows to render
 *   defaultSort   {Object}   — { key, direction: 'asc'|'desc' }
 *   onSortChange  {Function} — called with current sorts array [{ key, direction }]
 *   loading       {boolean}  — shows skeleton rows when true
 */
export function SortableTable({
  columns = [],
  data = [],
  defaultSort = null,
  onSortChange,
  loading = false,
}) {
  const [sorts, setSorts] = useState(defaultSort ? [defaultSort] : []);

  const handleHeaderClick = useCallback((col, e) => {
    if (col.sortable === false) return;
    const key = col.key;

    setSorts(prev => {
      let next;
      if (e.shiftKey && prev.length > 0) {
        // Shift+click → add / toggle secondary sort
        const existing = prev.find(s => s.key === key);
        if (existing) {
          next = prev.map(s =>
            s.key === key
              ? { ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' }
              : s
          );
        } else {
          next = [...prev, { key, direction: 'asc' }];
        }
      } else {
        // Single sort: toggle direction if already primary, else start asc
        const existing = prev[0];
        next = [{
          key,
          direction: existing?.key === key && existing.direction === 'asc' ? 'desc' : 'asc',
        }];
      }
      onSortChange?.(next);
      return next;
    });
  }, [onSortChange]);

  function getSortDir(key) {
    const s = sorts.find(s => s.key === key);
    return s ? s.direction : null;
  }

  function getSortIndex(key) {
    const idx = sorts.findIndex(s => s.key === key);
    return idx >= 0 ? idx + 1 : null;
  }

  // ── Skeleton loader ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div data-testid="sortable-table-loading" style={{ overflowX: 'auto' }}>
        <style>{`
          @keyframes shimmer {
            0%   { opacity: 1; }
            50%  { opacity: 0.4; }
            100% { opacity: 1; }
          }
        `}</style>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              {columns.map(col => (
                <th
                  key={col.key}
                  style={{
                    padding: '10px 12px',
                    textAlign: col.align || 'left',
                    color: '#94a3b8',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    background: '#f8fafc',
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                {columns.map((col, j) => (
                  <td key={col.key} style={{ padding: '11px 12px' }}>
                    <div style={{
                      height: 13,
                      borderRadius: 4,
                      background: '#e2e8f0',
                      width: `${55 + ((i * 7 + j * 11) % 35)}%`,
                      animation: `shimmer 1.6s ease-in-out infinite`,
                      animationDelay: `${(i * 0.1 + j * 0.05)}s`,
                    }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  if (!loading && data.length === 0) {
    return (
      <div
        data-testid="sortable-table-empty"
        style={{
          padding: '48px 24px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: 14,
        }}
      >
        No results match your current filters. Try adjusting or clearing filters.
      </div>
    );
  }

  // ── Data table ────────────────────────────────────────────────────────────
  return (
    <div data-testid="sortable-table" style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
            {columns.map(col => {
              const dir = getSortDir(col.key);
              const idx = getSortIndex(col.key);
              const isSortable = col.sortable !== false;
              return (
                <th
                  key={col.key}
                  data-testid={`sort-header-${col.key}`}
                  onClick={isSortable ? e => handleHeaderClick(col, e) : undefined}
                  title={isSortable ? 'Click to sort · Shift+click for secondary sort' : undefined}
                  style={{
                    padding: '10px 12px',
                    textAlign: col.align || 'left',
                    color: dir ? '#6366f1' : '#64748b',
                    fontWeight: 600,
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                    cursor: isSortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    background: dir ? '#f5f5ff' : '#f8fafc',
                    transition: 'background 0.12s',
                    borderBottom: dir ? '2px solid #6366f1' : '2px solid #e2e8f0',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.label}
                    {isSortable && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                        {dir === 'asc'
                          ? <ArrowUp size={11} />
                          : dir === 'desc'
                            ? <ArrowDown size={11} />
                            : <ArrowUpDown size={11} style={{ opacity: 0.3 }} />
                        }
                        {idx !== null && sorts.length > 1 && (
                          <span style={{
                            fontSize: 9, fontWeight: 700,
                            background: '#6366f1', color: '#fff',
                            borderRadius: 8, padding: '0 4px',
                          }}>
                            {idx}
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr
              key={row.id ?? row._key ?? i}
              data-testid={`table-row-${i}`}
              style={{
                borderBottom: '1px solid #f1f5f9',
                background: i % 2 === 0 ? '#fff' : '#fafbfc',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'; }}
            >
              {columns.map(col => (
                <td
                  key={col.key}
                  style={{
                    padding: '9px 12px',
                    color: '#1e293b',
                    textAlign: col.align || 'left',
                    maxWidth: col.maxWidth || 280,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: col.wrap ? 'normal' : 'nowrap',
                    verticalAlign: 'middle',
                  }}
                >
                  {col.render
                    ? col.render(row[col.key], row)
                    : row[col.key] !== null && row[col.key] !== undefined
                      ? row[col.key]
                      : '—'
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
